// ============================================================================
// STOCK LOCATIONS (ADR-0058 slice 1) — where stock is kept. Per entity
// (X-Entity-ID); every location belongs to a business unit of the entity.
// The IN_TRANSIT location is system-provisioned with the first storage
// location: it is listed and viewable but never editable or deletable.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAuthContext } from 'contexts/AuthContext';
import { useStockLocations } from 'hooks/api';
import { stockLocationService } from 'api/services';
import type {
  StockLocationDto, StockLocationQueryParams, StockLocationType, CreateStockLocationRequest,
} from 'api/types';

type ViewMode = 'list' | 'grid';

const ICONS = IconSystem as unknown as Record<string, React.ComponentType<{ size?: number }>>;

interface ApiError { response?: { status?: number; data?: { message?: string } } }
const apiStatus = (err: unknown) => (err as ApiError | null)?.response?.status;
const apiMessage = (err: unknown, fallback: string) =>
  (err as ApiError | null)?.response?.data?.message || fallback;
const CONCURRENCY_MESSAGE =
  'This location was changed by someone else since you opened it. Close the form and reopen it to get the latest version.';

const TRANSIT_INFO =
  'Created automatically with the first stock location. Holds goods between a transfer\'s issue and its receipt.';

const TYPE_LABELS: Record<StockLocationType, string> = {
  STORAGE: 'Storage',
  IN_TRANSIT: 'In transit (system)',
};
const TYPE_OPTIONS: SelectOption[] = [
  { value: 'STORAGE', label: TYPE_LABELS.STORAGE },
  { value: 'IN_TRANSIT', label: TYPE_LABELS.IN_TRANSIT },
];

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const buDisplay = (r: StockLocationDto) =>
  r.businessUnitName
    ? (r.businessUnitCode ? `${r.businessUnitCode} - ${r.businessUnitName}` : r.businessUnitName)
    : (r.businessUnitCode || '');

/** The selected entity's accessible business units as select options. */
function useEntityBuOptions() {
  const { accessibleBusinessUnits, selectedEntity, selectedBusinessUnit } = useAuthContext();
  const options: SelectOption[] = useMemo(() => {
    const list = selectedEntity
      ? accessibleBusinessUnits.filter(b => b.entityId === selectedEntity.id)
      : accessibleBusinessUnits;
    return list.map(b => ({ value: b.id, label: `${b.buCode} - ${b.buName}` }));
  }, [accessibleBusinessUnits, selectedEntity]);
  const defaultBuId = selectedBusinessUnit && options.some(o => o.value === selectedBusinessUnit.id)
    ? selectedBusinessUnit.id
    : '';
  return { options, defaultBuId };
}

interface FormData {
  businessUnitId: string;
  locationCode: string;
  locationName: string;
  isSaleable: boolean;
  address: string;
  description: string;
  statusCode: string;
}

const EMPTY: FormData = {
  businessUnitId: '', locationCode: '', locationName: '', isSaleable: true,
  address: '', description: '', statusCode: 'ACTIVE',
};

// ---------------------------------------------------------------------------
// FORM MODAL
// ---------------------------------------------------------------------------

interface LocationFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: StockLocationDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const LocationFormModal: React.FC<LocationFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const { options: buOptions, defaultBuId } = useEntityBuOptions();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const formRef = useRef(form); formRef.current = form;
  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const defaultBuIdRef = useRef(defaultBuId); defaultBuIdRef.current = defaultBuId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const r = recordRef.current;
    const m = modeRef.current;
    const data: FormData = r && m !== 'create'
      ? {
          businessUnitId: r.businessUnitId,
          locationCode: m === 'copy' ? '' : r.locationCode,
          locationName: m === 'copy' ? `${r.locationName} (Copy)` : r.locationName,
          isSaleable: r.isSaleable,
          address: r.address || '',
          description: r.description || '',
          statusCode: r.statusCode || 'ACTIVE',
        }
      : { ...EMPTY, businessUnitId: defaultBuIdRef.current };
    setForm(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(form) !== initialSnapshotRef.current;
  }, [form]);

  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(prev => {
      if (!prev[f]) return prev;
      const next = { ...prev }; delete next[f]; return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentMode = modeRef.current;
    const currentInstanceId = createInstanceIdRef.current || '1';
    const currentCode = recordRef.current?.locationCode;
    const savedForm = { ...formRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedForm) !== savedSnapshot : false;

    const modalId = currentMode === 'create'
      ? `${tabId}-form-modal-create-${currentInstanceId}`
      : `${tabId}-form-modal-${currentMode}-${currentCode}`;

    const title = currentMode === 'create'
      ? (savedForm.locationCode ? `Add - ${savedForm.locationCode} (${currentInstanceId})` : `Add New (${currentInstanceId})`)
      : currentMode === 'copy'
        ? `Copy - ${currentCode}`
        : `Edit - ${currentCode}`;

    minimize({
      id: modalId, title, moduleName, tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentInstanceId : undefined,
          currentMode !== 'create' ? currentCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const checkCode = useCallback(async (code: string): Promise<boolean> => {
    if (!code) return true;
    try {
      const excludeId = modeRef.current === 'edit' ? recordRef.current?.id : undefined;
      const ok = await stockLocationService.isCodeAvailable(code, excludeId);
      if (!ok) setFieldErrors(p => ({ ...p, locationCode: 'This location code is already in use.' }));
      return ok;
    } catch {
      return true; // the server enforces uniqueness on save
    }
  }, []);

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!form.businessUnitId) errors.businessUnitId = 'Business unit is required';
    if (!form.locationCode.trim()) errors.locationCode = 'Location code is required';
    if (!form.locationName.trim()) errors.locationName = 'Location name is required';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    if (!(await checkCode(form.locationCode.trim()))) return;
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, checkCode]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const payload: CreateStockLocationRequest = {
        businessUnitId: form.businessUnitId,
        locationCode: form.locationCode.trim(),
        locationName: form.locationName.trim(),
        isSaleable: form.isSaleable,
        address: form.address.trim() || null,
        description: form.description.trim() || null,
        statusCode: form.statusCode,
      };
      if (mode === 'edit' && record) {
        await stockLocationService.update(record.id, { ...payload, rowVersion: record.rowVersion });
      } else {
        await stockLocationService.create(payload);
      }
      setSaving(false);
      notificationService.toast.success(
        `Stock location "${payload.locationName}" ${mode === 'edit' ? 'updated' : 'created'}.`,
      );
      // Refetch: on the entity's first create the server also provisions the transit location.
      onSaved();
      onClose();
    } catch (err: unknown) {
      setSaving(false);
      if (apiStatus(err) === 409) {
        notificationService.toast.error(apiMessage(err, CONCURRENCY_MESSAGE));
        onSaved();
      } else {
        notificationService.toast.error(apiMessage(err, 'Could not save the stock location.'));
      }
    }
  }, [form, mode, record, onSaved, onClose]);

  if (!isOpen) return null;

  const hintStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' };
  // Keep the record's own BU visible even if the user no longer has it in scope.
  const buSeed: SelectOption[] | undefined = record && form.businessUnitId === record.businessUnitId
    && !buOptions.some(o => o.value === record.businessUnitId)
    ? [{ value: record.businessUnitId, label: buDisplay(record) }]
    : undefined;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add' : mode === 'copy' ? 'Copy' : 'Edit'} Stock Location
          </h2>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="businessUnitId">
            <SyutSelect
              label={<>Business Unit <span className="text-danger">*</span></>}
              options={buSeed ? [...buSeed, ...buOptions] : buOptions}
              value={form.businessUnitId}
              onChange={v => { clearFieldError('businessUnitId'); setForm(p => ({ ...p, businessUnitId: (v as string) || '' })); }}
              placeholder="Select business unit"
              isClearable={false}
              error={getFieldError('businessUnitId')}
              menuPortalTarget={document.body}
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="locationCode">
              <label className="form-label">Location Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('locationCode') ? ' form-field-error' : ''}`}
                value={form.locationCode}
                maxLength={30}
                placeholder="e.g. MAIN-WH"
                onChange={e => { clearFieldError('locationCode'); setForm(p => ({ ...p, locationCode: e.target.value.toUpperCase() })); }}
                onBlur={e => { void checkCode(e.target.value.trim()); }}
              />
              {getFieldError('locationCode') && <div className="form-error-message">{getFieldError('locationCode')}</div>}
            </div>

            <div className="form-group" data-field="locationName">
              <label className="form-label">Location Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('locationName') ? ' form-field-error' : ''}`}
                value={form.locationName}
                maxLength={150}
                placeholder="e.g. Main warehouse"
                onChange={e => { clearFieldError('locationName'); setForm(p => ({ ...p, locationName: e.target.value })); }}
              />
              {getFieldError('locationName') && <div className="form-error-message">{getFieldError('locationName')}</div>}
            </div>

            <div className="form-group" data-field="isSaleable">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', marginTop: '1.6rem' }}>
                <input
                  type="checkbox"
                  checked={form.isSaleable}
                  onChange={e => setForm(p => ({ ...p, isSaleable: e.target.checked }))}
                /> Saleable
              </label>
              <div style={hintStyle}>Untick for damaged or quarantine stores: their stock cannot be sold</div>
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                options={STATUS_OPTIONS}
                value={form.statusCode}
                onChange={val => setForm(p => ({ ...p, statusCode: (val as string) || 'ACTIVE' }))}
                isClearable={false}
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          <div className="form-group" data-field="address">
            <label className="form-label">Address</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={form.address}
              placeholder="Where the location is"
              onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
            />
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={form.description}
              placeholder="What is kept here"
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { void handleSave(); }} disabled={saving}>
            <IconSystem.check size={16} /> {mode === 'edit' ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`${mode === 'edit' ? 'Save changes to' : 'Create'} stock location "${form.locationName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// VIEW MODAL
// ---------------------------------------------------------------------------

const ViewField: React.FC<{ label: string; value?: string | number | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px', whiteSpace: 'pre-wrap',
    }}>
      {value !== null && value !== undefined && value !== '' ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

interface LocationViewModalProps {
  isOpen: boolean;
  record: StockLocationDto | null;
  onClose: () => void;
  onEdit?: (r: StockLocationDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (code: string) => void;
}

const LocationViewModal: React.FC<LocationViewModalProps> = ({
  isOpen, record, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !record) return;
    minimize({
      id: `${tabId}-view-modal-${record.locationCode}`,
      title: `View - ${record.locationCode}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(record.locationCode),
    });
    onClose();
  }, [tabId, moduleName, record, minimize, onClose, onRestoreView]);

  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Stock Location Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          {record.isSystem && (
            <div style={{
              display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.625rem 0.75rem',
              marginBottom: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: '0.8125rem',
            }}>
              <IconSystem.info size={16} />
              <span>{TRANSIT_INFO}</span>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Location Code" value={record.locationCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>
                <span className="badge" style={record.statusColor ? { background: record.statusColor, color: '#fff' } : undefined}>
                  {record.statusName || record.statusCode || 'Active'}
                </span>
              </div>
            </div>
            <ViewField label="Location Name" value={record.locationName} fullWidth />
            <ViewField label="Business Unit" value={buDisplay(record)} />
            <ViewField label="Type" value={TYPE_LABELS[record.locationType] ?? record.locationType} />
            <ViewField label="Saleable" value={record.isSaleable ? 'Yes' : 'No'} />
            <ViewField label="Default for Items" value={record.defaultForItemCount ?? 0} />
            <ViewField label="Address" value={record.address} fullWidth />
            <ViewField label="Description" value={record.description} fullWidth />
            <ViewField label="Created By" value={record.createdByName} />
            <ViewField label="Created At" value={record.createdAt ? new Date(record.createdAt).toLocaleString() : null} />
            {record.updatedAt && (
              <>
                <ViewField label="Updated By" value={record.updatedByName} />
                <ViewField label="Updated At" value={new Date(record.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && !record.isSystem && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(record); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// SCREEN
// ---------------------------------------------------------------------------

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'locationCode', label: 'Code' },
  { id: 'locationName', label: 'Name' },
  { id: 'businessUnit', label: 'Business Unit' },
  { id: 'locationType', label: 'Type' },
  { id: 'isSaleable', label: 'Saleable' },
  { id: 'defaultForItemCount', label: 'Default for Items' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface StockLocationScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const StockLocationScreen: React.FC<StockLocationScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('stock-location');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const { options: buOptions } = useEntityBuOptions();

  const [queryParams, setQueryParams] = useState<StockLocationQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useStockLocations(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('stock-location-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [buFilter, setBuFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [saleableOnly, setSaleableOnly] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selected, setSelected] = useState<StockLocationDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<StockLocationDto | null>(null);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleEdit = useCallback((r: StockLocationDto) => {
    if (r.isSystem) return;
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.locationCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((r: StockLocationDto) => {
    if (r.isSystem) return;
    const modalId = tabId ? `${tabId}-form-modal-copy-${r.locationCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleView = useCallback((r: StockLocationDto) => {
    const viewId = tabId ? `${tabId}-view-modal-${r.locationCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setViewRecord(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((code: string) => {
    const rec = data.find(r => r.locationCode === code);
    if (rec) { setViewRecord(rec); setViewOpen(true); }
  }, [data]);

  const handleRestoreModal = useCallback(
    (m: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.locationCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleDelete = useCallback(async (r: StockLocationDto) => {
    if (r.isSystem) return;
    const inUse = r.defaultForItemCount ?? 0;
    const ok = await confirmDelete({
      resourceLabel: r.locationName,
      message: inUse > 0
        ? `"${r.locationName}" is the default location for ${inUse} item${inUse === 1 ? '' : 's'}. The server will refuse the delete until they point elsewhere. Try anyway?`
        : undefined,
    });
    if (!ok) return;
    try {
      await stockLocationService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Stock location "${r.locationName}" deleted.`);
      refetch();
    } catch (err: unknown) {
      notificationService.toast.error(apiMessage(err, 'Could not delete the stock location.'));
      if (apiStatus(err) === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<StockLocationDto>[] = useMemo(() => [
    { id: 'locationCode', header: 'Code', accessorKey: 'locationCode', size: 130 },
    { id: 'locationName', header: 'Name', accessorKey: 'locationName', size: 220 },
    {
      id: 'businessUnit', header: 'Business Unit', accessorKey: 'businessUnitName', size: 200,
      cell: ({ row }) => (row.original as StockLocationDto).businessUnitName || '—',
    },
    {
      id: 'locationType', header: 'Type', accessorKey: 'locationType', size: 160,
      cell: ({ row }) => {
        const r = row.original as StockLocationDto;
        return r.locationType === 'IN_TRANSIT'
          ? <span className="badge" title={TRANSIT_INFO}>{TYPE_LABELS.IN_TRANSIT}</span>
          : (TYPE_LABELS[r.locationType] ?? r.locationType);
      },
    },
    {
      id: 'isSaleable', header: 'Saleable', accessorKey: 'isSaleable', size: 100,
      cell: ({ row }) => ((row.original as StockLocationDto).isSaleable ? 'Yes' : 'No'),
    },
    {
      id: 'defaultForItemCount', header: 'Default for Items', accessorKey: 'defaultForItemCount', size: 140, align: 'right',
      cell: ({ row }) => (row.original as StockLocationDto).defaultForItemCount ?? 0,
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => {
        const r = row.original as StockLocationDto;
        return (
          <span className="badge" style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
            {r.statusName || 'Active'}
          </span>
        );
      },
    },
  ], []);

  const rowActions = useCallback((r: StockLocationDto): ActionMenuItem[] => {
    // Order: View → Copy → Edit → Delete. View is never gated.
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    // The system transit location is view-only.
    if (r.isSystem) return items;
    if (can('CREATE')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => { void handleDelete(r); }, variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Location'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const appliedCount = (queryParams.businessUnitId ? 1 : 0) + (queryParams.locationType ? 1 : 0)
    + (queryParams.saleableOnly ? 1 : 0) + (queryParams.includeInactive ? 1 : 0);
  const HeaderIcon = moduleIcon ? ICONS[moduleIcon] : undefined;

  return (
    <EnterpriseScreenShell
      title="Stock Locations"
      description="Where stock is kept: each location belongs to a business unit of the entity"
      icon={HeaderIcon ? <HeaderIcon size={18} /> : <IconSystem.home size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search locations...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Business Unit"
                  options={buOptions}
                  value={buFilter}
                  onChange={v => setBuFilter((v as string) || '')}
                  placeholder="All business units"
                  isClearable
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Type"
                  options={TYPE_OPTIONS}
                  value={typeFilter}
                  onChange={v => setTypeFilter((v as string) || '')}
                  placeholder="All types"
                  isClearable
                  menuPortalTarget={document.body}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={saleableOnly}
                    onChange={e => setSaleableOnly(e.target.checked)} /> Saleable only
                </label>
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setBuFilter(''); setTypeFilter(''); setSaleableOnly(false); setIncludeInactive(false);
                setQueryParams(p => ({ page: 1, pageSize: p.pageSize, searchTerm: p.searchTerm }));
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setQueryParams(p => ({
                  ...p,
                  businessUnitId: buFilter || undefined,
                  locationType: (typeFilter || undefined) as StockLocationType | undefined,
                  saleableOnly: saleableOnly || undefined,
                  includeInactive: includeInactive || undefined,
                  page: 1,
                }));
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No stock locations yet — click Add.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {r.locationCode}
                        {r.isSystem && <span className="badge" style={{ marginLeft: '0.4rem' }}>{TYPE_LABELS.IN_TRANSIT}</span>}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.locationName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{r.businessUnitName || '—'}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {r.isSaleable ? 'Saleable' : 'Not saleable'} · default for {r.defaultForItemCount ?? 0} item{(r.defaultForItemCount ?? 0) === 1 ? '' : 's'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <LocationFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Stock Locations'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />

      <LocationViewModal isOpen={viewOpen} record={viewRecord}
        onClose={() => { setViewOpen(false); setViewRecord(null); }}
        onEdit={can('UPDATE') ? handleEdit : undefined}
        tabId={tabId} moduleName={moduleName || 'Stock Locations'}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default StockLocationScreen;
